/**
 * Jovii — the handful of dates these three screens have to put into words.
 *
 * `$lib/ui/format.ts` already has `ago()` and `airDate()` and they are used wherever
 * they fit. What is here is what it does not have: a countdown FORWARD (the handle
 * cooldown), a retention window in months and years, and the two halves of an evening —
 * which night it was, and how long it went on.
 *
 * WHY NOT `Intl.RelativeTimeFormat`. It says "in 30 days" perfectly well, and then says
 * "in 0 days" for anything under one, which is the exact case a person is most likely to
 * be staring at. Rounding rules that read well are worth eleven lines.
 *
 * Every function here is pure and takes its `now`, so the tests are not flaky at
 * midnight.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * How long until something becomes possible. For the once-a-month handle change, where
 * the number is the SERVER'S remainder and must not be recomputed from a date this
 * browser worked out for itself.
 */
export function until(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return 'now';
  if (ms < 90 * 1000) return 'in a moment';
  if (ms < HOUR) return `in ${Math.round(ms / MINUTE)} minutes`;
  if (ms < DAY) {
    const h = Math.round(ms / HOUR);
    return h === 1 ? 'in an hour' : `in ${h} hours`;
  }
  const d = Math.ceil(ms / DAY);
  if (d === 1) return 'tomorrow';
  if (d < 45) return `in ${d} days`;
  const months = Math.round(d / 30);
  return months === 1 ? 'in a month' : `in ${months} months`;
}

/** The same instant as a date, for the "and here is exactly when" half. */
export function onDate(at: number): string {
  if (!Number.isFinite(at)) return '';
  return new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/** A length of time as a plain window: "a year", "6 months", "30 days". */
export function span(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const days = Math.round(ms / DAY);
  if (days >= 360 && days <= 370) return 'a year';
  if (days % 365 === 0 && days >= 730) return `${days / 365} years`;
  if (days >= 60) return `${Math.round(days / 30)} months`;
  return `${days} days`;
}

/**
 * Which evening this was. "Today" and "Yesterday" earn their place because a room
 * history is read mostly about the last two nights; anything older gets a date, and
 * anything in another year gets the year too.
 */
export function dayLabel(at: number, now = Date.now()): string {
  if (!Number.isFinite(at)) return '';
  const then = new Date(at);
  const today = new Date(now);
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  if (at >= midnight) return 'Today';
  if (at >= midnight - DAY) return 'Yesterday';
  const sameYear = then.getFullYear() === today.getFullYear();
  return then.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' })
  });
}

/**
 * How long a visit lasted. Under two minutes it says so rather than rounding to "0m",
 * because "I opened it and left" is a real and common row.
 */
export function lasted(fromMs: number, toMs: number): string {
  const ms = toMs - fromMs;
  if (!Number.isFinite(ms) || ms < 2 * MINUTE) return 'a moment';
  const mins = Math.round(ms / MINUTE);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
